import SectionCard from "../../components/ui/SectionCard";
import Button from "../../components/ui/Button";
import LoadingState from "../../components/ui/LoadingState";
import ErrorState from "../../components/ui/ErrorState";
import EmptyState from "../../components/ui/EmptyState";
import ExchangeRateHealthBadge from "./ExchangeRateHealthBadge";
import SearchableCurrencySelect from "../../components/ui/SearchableCurrencySelect";

function textOrDash(value) {
  if (value === undefined || value === null || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "Not scheduled yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function ExchangeRateSyncPanel({
  syncRows,
  loading,
  error,
  autoSyncStatus,
  baseCurrency,
  supportedCurrencies,
  syncing,
  fullSyncProgress,
  onBaseCurrencyChange,
  onReload,
  onSync,
  onFullSync,
}) {
  if (loading && (!syncRows || syncRows.length === 0)) {
    return <LoadingState text="Loading sync status..." />;
  }

  if (error && (!syncRows || syncRows.length === 0)) {
    return (
      <ErrorState
        text={error}
        action={
          <button type="button" className="underline" onClick={onReload}>
            Retry
          </button>
        }
      />
    );
  }

  return (
    <SectionCard
      title="Sync & Providers"
      subtitle="Provider status and manual synchronization controls"
      actions={
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">
              Base currency
            </span>
            <SearchableCurrencySelect
              className="w-56"
              id="exchange-rate-base-currency"
              value={baseCurrency}
              options={supportedCurrencies}
              onChange={onBaseCurrencyChange}
              emptyLabel="Select base currency"
              searchPlaceholder="Search currency"
              searchAriaLabel="Search currency"
              selectClassName="rounded-md border border-outline bg-surface px-2 py-1 text-sm"
            />
          </label>
          <Button variant="secondary" onClick={onReload}>
            Reload
          </Button>
          <Button onClick={onSync} disabled={syncing}>
            {syncing ? "Syncing..." : "Run sync now"}
          </Button>
          <Button variant="secondary" onClick={onFullSync} disabled={syncing}>
            {syncing ? "Syncing..." : "Run full sync"}
          </Button>
        </div>
      }
    >
      {autoSyncStatus ? (
        <div className="mb-3 rounded-lg border border-outline bg-surface-elevated px-3 py-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Auto sync</span>{" "}
          {autoSyncStatus.enabled ? "enabled" : "disabled"}
          {" · "}
          {autoSyncStatus.allBaseCurrencies
            ? "all base currencies"
            : `base ${autoSyncStatus.baseCurrency || "EUR"}`}
          {" · "}
          next run {formatDateTime(autoSyncStatus.nextRunAt)}
          {" · "}
          last run {formatDateTime(autoSyncStatus.lastRunFinishedAt)}
        </div>
      ) : null}
      {fullSyncProgress ? (
        <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-foreground">
              Full sync progress
            </span>
            <span className="text-muted-foreground">
              {Number(fullSyncProgress.processed || 0)} /{" "}
              {Number(fullSyncProgress.total || 0) || "?"} bases
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-outline">
            <div
              className="h-full bg-primary transition-all"
              style={{
                width: `${Math.max(
                  2,
                  Math.min(
                    100,
                    Number(fullSyncProgress.total || 0) > 0
                      ? (Number(fullSyncProgress.processed || 0) /
                          Number(fullSyncProgress.total || 1)) *
                          100
                      : 2,
                  ),
                )}%`,
              }}
            />
          </div>
          <div className="mt-2 grid gap-2 text-muted-foreground sm:grid-cols-4">
            <span>
              Synced rows: {Number(fullSyncProgress.syncedCount || 0)}
            </span>
            <span>Missing: {Number(fullSyncProgress.missingCount || 0)}</span>
            <span>
              Failed bases: {Number(fullSyncProgress.failedCount || 0)}
            </span>
            <span>Remaining: {Number(fullSyncProgress.remaining || 0)}</span>
          </div>
          {fullSyncProgress.baseCurrency ? (
            <div className="mt-1 text-xs text-muted-foreground">
              Last base: {fullSyncProgress.baseCurrency}
            </div>
          ) : null}
        </div>
      ) : null}
      {!syncRows || syncRows.length === 0 ? (
        <EmptyState
          title="No provider state"
          description="Run manual sync to initialize provider status."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-outline bg-surface">
          <table
            className="min-w-full text-sm"
            aria-label="Provider sync status"
          >
            <thead className="bg-table-header">
              <tr>
                <th className="px-3 py-2 text-left">Provider</th>
                <th className="px-3 py-2 text-left">Base</th>
                <th className="px-3 py-2 text-left">Last attempt</th>
                <th className="px-3 py-2 text-left">Last success</th>
                <th className="px-3 py-2 text-left">Failures</th>
                <th className="px-3 py-2 text-left">Last error</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline">
              {syncRows.map((row, index) => {
                const health =
                  Number(row.consecutiveFailures || 0) > 0 || row.lastError
                    ? "failed"
                    : "fresh";
                return (
                  <tr key={`${row.source}-${row.baseCurrency}-${index}`}>
                    <td className="px-3 py-2">{textOrDash(row.source)}</td>
                    <td className="px-3 py-2">
                      {textOrDash(row.baseCurrency)}
                    </td>
                    <td className="px-3 py-2">
                      {textOrDash(row.lastAttemptAt, "Not synced yet")}
                    </td>
                    <td className="px-3 py-2">
                      {textOrDash(row.lastSuccessfulSyncAt, "Not synced yet")}
                    </td>
                    <td className="px-3 py-2">
                      {Number(row.consecutiveFailures || 0)}
                    </td>
                    <td className="px-3 py-2">{textOrDash(row.lastError)}</td>
                    <td className="px-3 py-2">
                      <ExchangeRateHealthBadge health={health} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
